import { v4 as uuid } from 'uuid';
import { useEffect, useState } from 'react';
import {
  Box as Item,
  FormLabel,
  Button,
  Typography,
} from '@mui/material';
import Plan from '../../../../models/Plan/Plan';
import StatusStyle from './StatusStyle';
import ModalComponent from '../../../../components/ModalComponent';
import { useUserContext } from '../../../../contexts/UserContext';

// Stripe status to Japanese mapping
const stripeStatusToJapanese: { [key: string]: string } = {
  active: 'Active',
  trialing: 'Trialing',
  past_due: 'Past due',
  canceled: 'Canceled',
  incomplete: 'Incomplete',
  incomplete_expired: 'Expired',
  unpaid: 'Unpaid',
  paused: 'Paused',
};

function PlanCard({
  plan,
  total,
  setTriedToRegisterToPlan,
  setSelectedPlan,
  setActivePlan,
  customOnClickEventFor,
  singlePagesCount,
}: {
  plan: Plan,
  total: number,
  setTriedToRegisterToPlan: (tried: boolean) => void,
  setSelectedPlan: (plan: Plan) => void,
  setActivePlan: (plan: Plan) => void,
  customOnClickEventFor?: (plan: Plan) => void,
  singlePagesCount?: number,
}) {
  // variables
  const [cardStyle, setCardStyle] = useState({});
  const [displayMessage, setDisplayMessage] = useState('');
  const [isDeactivated, setIsDeactivated] = useState(false);
  const [showCantReturnModal, setShowCantReturnModal] = useState(false);
  const [modalContent, setModalContent] = useState('');
  const { user } = useUserContext();

  // Plan type identification (avoid hardcoded plan name strings)
  const isFreePlan = plan.price === 0;
  const isSinglePagePlan = plan.metadata?.type === 'singlePage';

  const invoicePlanId = process.env.REACT_APP_INVOICE_PLAN_ID ?? null;

  // effects
  useEffect(() => {
    if (user.is_invoice_mode_enabled) {
      if (plan.id === invoicePlanId) {
        setCardStyle(StatusStyle.isActivePlan);

        // Show status in Japanese if not active or trialing
        if (plan.stripe_status && plan.stripe_status !== 'active' && plan.stripe_status !== 'trialing') {
          const statusInJapanese = stripeStatusToJapanese[plan.stripe_status] || plan.stripe_status;
          setDisplayMessage(`In use (${statusInJapanese})`);
        } else {
          setDisplayMessage('In use');
        }

        setIsDeactivated(true);
        setActivePlan(plan);
        return;
      }

      setModalContent('Plan change is not available while invoice mode is enabled.');
      setCardStyle(StatusStyle.isLowerPlan);
      setDisplayMessage('Not selectable');
      setIsDeactivated(true);
      return;
    }

    // Add check for free plan with single pages
    if (isFreePlan && singlePagesCount > 0) {
      setCardStyle(StatusStyle.isLowerPlan);
      setDisplayMessage('Not selectable');
      setModalContent('You cannot switch to the free plan while you have single pages.');
      setIsDeactivated(true);
      return;
    }

    // Reset free plan state when no single pages
    if (isFreePlan && plan.isActivePlan) {
      setCardStyle(StatusStyle.isLowerPlan);
      setCardStyle(StatusStyle.isActivePlan);
      setDisplayMessage('Switch to this plan');
      setIsDeactivated(true);
      setModalContent('');
      setActivePlan(plan);
      return;
    }

    if (plan.isActivePlan && isSinglePagePlan) {
      setActivePlan(plan);
      setIsDeactivated(false);
      setCardStyle(StatusStyle.isActivePlan);

      // Show status in Japanese if not active or trialing
      if (plan.stripe_status && plan.stripe_status !== 'active' && plan.stripe_status !== 'trialing') {
        const statusInJapanese = stripeStatusToJapanese[plan.stripe_status] || plan.stripe_status;
        setDisplayMessage(`In use (${statusInJapanese})`);
      } else {
        setDisplayMessage('In use');
      }
    } else if (plan.isActivePlan && !isSinglePagePlan) {
      setActivePlan(plan);
      setCardStyle(StatusStyle.isActivePlan);

      // Show status in Japanese if not active or trialing
      if (plan.stripe_status && plan.stripe_status !== 'active' && plan.stripe_status !== 'trialing') {
        const statusInJapanese = stripeStatusToJapanese[plan.stripe_status] || plan.stripe_status;
        setDisplayMessage(`In use (${statusInJapanese})`);
      } else {
        setDisplayMessage('In use');
      }

      setIsDeactivated(true);
    } else if (plan.isLowerPlan) {
      setCardStyle(StatusStyle.isLowerPlan);
      setDisplayMessage('Not selectable');
      setModalContent('The number of created pages exceeds the limit.');
      setIsDeactivated(true);

      const minimumPagesToChange = parseInt(plan?.metadata?.feat_pages ?? 0, 10) + singlePagesCount;

      if (total <= minimumPagesToChange && !isFreePlan) {
        setCardStyle(StatusStyle.isNotLowerPlan);

        // Check if this is a single page plan
        if (isSinglePagePlan) {
          if (plan.quantity_of_acquired_single_pages > 0) {
            setDisplayMessage('Change');
          } else {
            setDisplayMessage('Add');
          }
        } else {
          setDisplayMessage('Switch to this plan');
        }

        setIsDeactivated(false);
      }
    } else {
      setCardStyle(StatusStyle.isNotLowerPlan);

      // Check if this is a single page plan
      if (isSinglePagePlan) {
        if (plan.quantity_of_acquired_single_pages > 0) {
          setDisplayMessage('Change');
        } else {
          setDisplayMessage('Add');
        }
      } else {
        setDisplayMessage('Switch to this plan');
      }
    }
  }, [singlePagesCount, plan.isActivePlan, user.is_invoice_mode_enabled, total, plan, invoicePlanId, setActivePlan, setSelectedPlan]);

  // functions
  const handleRegisterPlan = (plan: Plan) => {
    setSelectedPlan(plan);
    setTriedToRegisterToPlan(true);
  };

  const handleShowCantReturnModal = () => {
    if (isDeactivated && !plan.isActivePlan) setShowCantReturnModal(!showCantReturnModal);
  };

  // function displayPrice(): import('react').ReactNode {
  //   let price: React.ReactNode | string = (
  //     <>
  //       {plan.formattedPrice}
  //       <Typography
  //         position="relative"
  //         bottom={0}
  //         padding={1}
  //         fontSize={12}
  //         color="grey"
  //       >
  //         / 月(税込)
  //       </Typography>
  //     </>
  //   );

  //   if (user.is_invoice_mode_enabled && plan.id === invoicePlanId) {
  //     price = (
  //       <span
  //         style={{ fontSize: '0.8em' }}
  //       >
  //         お問い合わせ下さい
  //       </span>
  //     );
  //   }

  //   return (
  //     <>
  //       <Item
  //         sx={{
  //           display: 'flex',
  //           flexDirection: 'row',
  //           alignItems: 'center',
  //           justifyContent: 'center',
  //         }}
  //       >
  //         {price}
  //       </Item>
  //     </>
  //   );
  // }

  return (
    <Item
      sx={cardStyle}
    >
      <Item className="card-header">
        <p>{plan.description}</p>
        <FormLabel
          sx={{
            color: 'GrayText',
            fontSize: 12,
          }}
        >
          {plan.metadata?.additional_description}
        </FormLabel>
        <h3 className="text-bold my-2">{plan.name}</h3>
      </Item>
      <Item className="card-body">
        {/* <Item>
          <h1 className="text-bold">
            {displayPrice()}
          </h1>
        </Item> */}
        <Item sx={{ minHeight: 75 }}>
          {plan.features?.map((feature: any) => (
            <div key={uuid()}>
              {feature.name}
            </div>
          ))}

          {plan?.quantity_of_acquired_single_pages > 0 && (
            <div className="mt-3 py-1 px-3 text-primary text-bold border border-3 border-danger rounded-3 d-inline-block">
              {`Currently ${plan?.quantity_of_acquired_single_pages} page(s) applied`}
            </div>
          )}
        </Item>

        <Item onClick={handleShowCantReturnModal}>
          <Button
            disabled={isDeactivated}
            className="mt-2 w-100"
            variant="contained"
            onClick={
              customOnClickEventFor
                ? () => customOnClickEventFor(plan)
                : () => handleRegisterPlan(plan)
            }
          >
            {displayMessage}
          </Button>
        </Item>
      </Item>

      <ModalComponent
        title="Plan change cannot be requested"
        show={showCantReturnModal}
        closeModal={handleShowCantReturnModal}
        onConfirm={handleShowCantReturnModal}
        onCloseModal={handleShowCantReturnModal}
        cancelButtonText="Close"
        hideConfirmButton
      >
        {modalContent}
      </ModalComponent>
    </Item>
  );
}

export default PlanCard;
